import { useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { setHours, setMinutes, isToday as isTodayFn, format } from "date-fns";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "@/types/calendar-view";
import { CalendarEventBlock } from "./CalendarEventBlock";
import { CalendarCurrentTimeLine } from "./CalendarCurrentTimeLine";

// ---------------------------------------------------------------------------
// CalendarDayColumn — single day column in the weekly grid
// ---------------------------------------------------------------------------

interface CalendarDayColumnProps {
  date: Date;
  events: CalendarEvent[];
  startHour: number;
  endHour: number;
  slotDuration: number; // minutes per slot (30)
  slotHeight: number; // px per slot
  compact?: boolean;
  selectedSlot?: Date; // highlight this time slot
  onSlotClick?: (startTime: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onResize?: (eventId: string, newDurationMin: number) => void;
}

// ---------------------------------------------------------------------------
// Overlap detection — assigns column index and total columns
// ---------------------------------------------------------------------------

interface LayoutEvent {
  event: CalendarEvent;
  top: number;
  height: number;
  col: number;
  totalCols: number;
}

function layoutEvents(
  events: CalendarEvent[],
  startHour: number,
  slotDuration: number,
  slotHeight: number
): LayoutEvent[] {
  if (events.length === 0) return [];

  const pxPerMinute = slotHeight / slotDuration;

  const items = events
    .map((event) => {
      const startMin =
        (event.start.getHours() - startHour) * 60 + event.start.getMinutes();
      const endMin =
        (event.end.getHours() - startHour) * 60 + event.end.getMinutes();
      const top = startMin * pxPerMinute;
      const height = Math.max((endMin - startMin) * pxPerMinute, 20);
      return { event, top, height, startMin, endMin };
    })
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  // Group overlapping items
  const groups: (typeof items)[] = [];
  let currentGroup: typeof items = [];

  for (const item of items) {
    if (
      currentGroup.length === 0 ||
      item.startMin < Math.max(...currentGroup.map((g) => g.endMin))
    ) {
      currentGroup.push(item);
    } else {
      groups.push(currentGroup);
      currentGroup = [item];
    }
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  // Assign column indices within each group
  const result: LayoutEvent[] = [];

  for (const group of groups) {
    const columns: number[] = []; // tracks endMin per column
    const assignments: number[] = [];

    for (const item of group) {
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        if (item.startMin >= columns[c]) {
          columns[c] = item.endMin;
          assignments.push(c);
          placed = true;
          break;
        }
      }
      if (!placed) {
        assignments.push(columns.length);
        columns.push(item.endMin);
      }
    }

    const totalCols = columns.length;
    group.forEach((item, i) => {
      result.push({
        event: item.event,
        top: item.top,
        height: item.height,
        col: assignments[i],
        totalCols,
      });
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// TimeSlotRow — a single 30min slot (droppable target)
// ---------------------------------------------------------------------------

function TimeSlotRow({
  slotTime,
  slotHeight,
  isOutsideWorking,
  isSelected,
  compact,
  onSlotClick,
}: {
  slotTime: Date;
  slotHeight: number;
  isOutsideWorking: boolean;
  isSelected: boolean;
  compact: boolean;
  onSlotClick?: (startTime: Date) => void;
}) {
  const slotId = `slot-${slotTime.toISOString()}`;
  const { setNodeRef, isOver } = useDroppable({ id: slotId, data: { slotTime } });
  const isHourMark = slotTime.getMinutes() === 0;

  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      aria-label={`${format(slotTime, "HH:mm")} slot`}
      className={cn(
        "relative border-b border-border/30 transition-colors",
        isHourMark && "border-b-border/60",
        isOutsideWorking && "bg-muted/30",
        isOver && "bg-primary/10",
        isSelected && "bg-primary/15 ring-1 ring-primary/30",
        !isSelected && !isOver && "hover:bg-muted/20"
      )}
      style={{ height: `${slotHeight}px` }}
      onClick={() => onSlotClick?.(slotTime)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSlotClick?.(slotTime);
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const CalendarDayColumn = ({
  date,
  events,
  startHour,
  endHour,
  slotDuration,
  slotHeight,
  compact = false,
  selectedSlot,
  onSlotClick,
  onEventClick,
  onResize,
}: CalendarDayColumnProps) => {
  const isToday = isTodayFn(date);

  // Generate time slots
  const slots = useMemo(() => {
    const result: Date[] = [];
    for (let h = startHour; h < endHour; h++) {
      for (let m = 0; m < 60; m += slotDuration) {
        result.push(setMinutes(setHours(new Date(date), h), m));
      }
    }
    return result;
  }, [date, startHour, endHour, slotDuration]);

  // Layout events with overlap handling
  const layoutItems = useMemo(
    () => layoutEvents(events, startHour, slotDuration, slotHeight),
    [events, startHour, slotDuration, slotHeight]
  );

  const workingStart = 8;
  const workingEnd = 18;

  return (
    <div className="relative flex-1 min-w-0">
      {/* Time slot rows */}
      {slots.map((slotTime) => {
        const hour = slotTime.getHours();
        const isOutsideWorking = hour < workingStart || hour >= workingEnd;
        const isSelected =
          !!selectedSlot &&
          slotTime.getHours() === selectedSlot.getHours() &&
          slotTime.getMinutes() === selectedSlot.getMinutes();

        return (
          <TimeSlotRow
            key={slotTime.toISOString()}
            slotTime={slotTime}
            slotHeight={slotHeight}
            isOutsideWorking={isOutsideWorking}
            isSelected={isSelected}
            compact={compact}
            onSlotClick={onSlotClick}
          />
        );
      })}

      {/* Positioned events */}
      <div className="absolute inset-0 pointer-events-none">
        {layoutItems.map((item) => {
          const widthPct = `calc(${100 / item.totalCols}% - 4px)`;
          const leftPct = `calc(${(item.col / item.totalCols) * 100}% + 2px)`;

          return (
            <div key={item.event.id} className="pointer-events-auto">
              <CalendarEventBlock
                event={item.event}
                top={item.top}
                height={item.height}
                width={widthPct}
                left={leftPct}
                compact={compact}
                slotHeight={slotHeight}
                onEventClick={onEventClick}
                onResize={onResize}
              />
            </div>
          );
        })}
      </div>

      {/* Current time line */}
      {isToday && (
        <CalendarCurrentTimeLine startHour={startHour} slotHeight={slotHeight} />
      )}
    </div>
  );
};
